import { getRecoil } from 'recoil-nexus';

import { postJoin } from '@/api/gameplay.service';
import { hydrateFromSnapshot, subscribeToGame } from '@/realtime/game-channel';
import { userState } from '@/framework/user';
import * as userSelector from '@/framework/user/userSelector';

import { pidState } from '@/use/usePidState';

type JoinSnapshot = {
  phase?: string;
  type?: string;
  turn?: number;
  players?: unknown;
};

function whenChannelReady(channel: {
  subscribed?: (cb: () => void) => unknown;
}): Promise<void> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, 1500);
    if (typeof channel.subscribed !== 'function') {
      window.clearTimeout(timer);
      resolve();
      return;
    }
    channel.subscribed(() => {
      window.clearTimeout(timer);
      resolve();
    });
  });
}

export async function connectToGameRoom(
  token: string,
  options?: { onGameStarted?: () => void },
): Promise<JoinSnapshot> {
  const pid = getRecoil(pidState);
  if (!pid) {
    throw new Error('Missing client pid');
  }

  const display_name = getRecoil(userSelector.loggedIn)
    ? getRecoil(userState.username)
    : undefined;

  let entered = false;
  const enter = () => {
    if (entered) return;
    entered = true;
    options?.onGameStarted?.();
  };

  const channel = subscribeToGame(token, { onGameStarted: enter });
  await whenChannelReady(channel);

  const joined = (await postJoin(token, {
    client_pid: pid,
    ...(display_name ? { display_name } : {}),
  })) as JoinSnapshot;

  hydrateFromSnapshot(joined as Record<string, unknown>);
  if (joined.phase && joined.phase !== 'lobby') {
    enter();
  }

  return joined;
}
